/* LPC product page: one custom element, no dependencies.

   Reads the product JSON that sections/lpc-main-product.liquid prints, then keeps price, availability,
   photo, caption and address bar in step with the colour and size the shopper picks. No server round trip.
   Add to cart posts to /cart/add.js and hands the result to Dawn's cart drawer (renderContents).
   Dawn's cart-drawer.js is the only theme script this file relies on. */
(() => {
  if (customElements.get('lpc-product')) return;

  const STAGE_WIDTHS = [600, 900, 1200, 1400];
  const ADD_ERROR = "We couldn't add that to your cart. Please try again.";
  const phone = window.matchMedia('(max-width: 700px)');

  const calmMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Only touch the DOM when the text really changes, so live regions stay quiet.
  const setText = (node, text) => {
    if (node && node.textContent !== text) node.textContent = text;
  };

  const setHidden = (node, hidden) => {
    if (node && node.hidden !== hidden) node.hidden = hidden;
  };

  const withWidth = (src, width) => {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', width);
    return url.toString();
  };

  // Shopify.formatMoney when the page has it, otherwise the same rules in a few lines.
  const formatMoney = (cents, format) => {
    const shopify = window.Shopify;
    if (shopify && typeof shopify.formatMoney === 'function') {
      return String(shopify.formatMoney(cents, format)).replace(/<[^>]*>/g, '');
    }
    const amount = (decimals, thousands, point) => {
      const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + point + fraction : grouped;
    };
    return String(format)
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
        switch (name) {
          case 'amount_no_decimals':
            return amount(0, ',', '.');
          case 'amount_with_comma_separator':
            return amount(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return amount(0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return amount(2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return amount(0, ' ', '.');
          case 'amount_with_space_separator':
            return amount(2, ' ', ',');
          case 'amount_with_period_and_space_separator':
            return amount(2, ' ', '.');
          default:
            return amount(2, ',', '.');
        }
      });
  };

  class LpcProduct extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setup();
      if (!this.ready) return;
      document.addEventListener('lpc:open-size-finder', this.onFinderEvent);
      document.addEventListener('lpc:select-size', this.onSelectSize);
      this.watchBar();
    }

    disconnectedCallback() {
      document.removeEventListener('lpc:open-size-finder', this.onFinderEvent);
      document.removeEventListener('lpc:select-size', this.onSelectSize);
      if (this.observer) this.observer.disconnect();
      if (this.titleObserver) this.titleObserver.disconnect();
      phone.removeEventListener('change', this.onPhoneChange);
      this.passed = false;
      this.syncBar();
    }

    setup() {
      const json = this.querySelector('[data-lpc-product]');
      if (!json) return;
      try {
        this.product = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }

      this.optionNames = (this.product.options || []).map((option) => (typeof option === 'string' ? option : option.name));
      this.variants = this.product.variants.map((variant) => ({
        id: variant.id,
        available: variant.available,
        price: variant.price,
        compare: variant.compare_at_price,
        values: Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3],
        mediaId: variant.featured_media ? variant.featured_media.id : null,
      }));
      this.colorIndex = this.optionNames.findIndex((name) => /^colou?r$/i.test(name.trim()));
      this.sizeIndex = this.optionNames.findIndex((name) => /size/i.test(name));
      this.single = this.dataset.single === 'true';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.fieldsets = [];
      this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        this.fieldsets[Number(fieldset.dataset.optionIndex)] = fieldset;
      });

      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.addButton = this.querySelector('[data-add]');
      this.errorBox = this.querySelector('[data-error]');
      this.priceFrom = this.querySelector('[data-price-from]');
      this.priceCurrent = this.querySelector('[data-price-current]');
      this.priceCompare = this.querySelector('[data-price-compare]');
      this.priceCompareLabel = this.querySelector('[data-price-compare-label]');
      this.stage = this.querySelector('.lpc-product__stage');
      this.stageImage = this.querySelector('.lpc-product__stage-img');
      this.caption = this.querySelector('[data-caption]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      this.zoomImage = this.querySelector('[data-zoom-img]');
      this.finder = this.querySelector('[data-size-finder]');
      this.bar = this.querySelector('[data-bar]');
      this.barButton = this.querySelector('[data-bar-add]');
      this.barPrice = this.querySelector('[data-bar-price]');
      this.barSelection = this.querySelector('[data-bar-selection]');
      // Not this.title: that is the element's own tooltip attribute, so assigning it writes a string.
      this.titleEl = this.querySelector('[data-fit-title]');

      // Which colour each photo belongs to, from the variants that use it.
      this.mediaColors = {};
      this.variants.forEach((variant) => {
        if (variant.mediaId && this.colorIndex >= 0 && !this.mediaColors[variant.mediaId]) {
          this.mediaColors[variant.mediaId] = variant.values[this.colorIndex];
        }
      });

      if (document.querySelector('cart-drawer')) {
        [this.addButton, this.barButton].forEach((button) => button && button.setAttribute('aria-haspopup', 'dialog'));
      }

      if (this.titleEl) {
        this.fitTitle();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitTitle());
        if ('ResizeObserver' in window) {
          // Width changes only: fitting changes the height, which must not re-trigger a fit.
          this.titleWidth = this.titleEl.parentElement.clientWidth;
          this.titleObserver = new ResizeObserver(() => {
            const width = this.titleEl.parentElement.clientWidth;
            if (width === this.titleWidth) return;
            this.titleWidth = width;
            cancelAnimationFrame(this.titleFrame);
            this.titleFrame = requestAnimationFrame(() => this.fitTitle());
          });
          this.titleObserver.observe(this.titleEl.parentElement);
        }
      }

      this.busy = false;
      this.passed = false;
      this.onFinderEvent = () => this.openFinder();
      this.onSelectSize = (event) => this.selectSize(event.detail && event.detail.size);
      this.onPhoneChange = () => this.syncBar();

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => {
        if (event.target.matches('.lpc-product__input')) this.onOptionChange(event.target);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.add(this.addButton);
      });
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          if (!this.querySelector('dialog[open]')) document.body.classList.remove('lpc-product-locked');
        });
      });

      this.ready = true;
      this.update();
    }

    /* ---------- Reading the choice ---------- */

    // One entry per option: the checked value, or null when nothing is chosen yet.
    get values() {
      return this.optionNames.map((name, index) => {
        const fieldset = this.fieldsets[index];
        const input = fieldset && fieldset.querySelector('input:checked');
        return input ? input.value : null;
      });
    }

    findVariant(values) {
      if (this.single) return this.variants[0];
      if (values.includes(null)) return null;
      return this.variants.find((variant) => values.every((value, index) => variant.values[index] === value)) || null;
    }

    /* ---------- One place that brings the page in line with the choice ---------- */

    update() {
      const values = this.values;
      this.complete = this.single || !values.includes(null);
      this.variant = this.findVariant(values);
      this.idInput.value = this.variant ? this.variant.id : '';

      this.refreshOptions(values);
      this.refreshPrice(values);
      this.refreshButtons();
      this.refreshAddress();
      this.refreshBarSelection(values);
    }

    refreshOptions(values) {
      this.fieldsets.forEach((fieldset, index) => {
        if (!fieldset) return;
        setText(fieldset.querySelector('[data-selected-value]'), values[index] || '\u2014');
        fieldset.querySelectorAll('input').forEach((input) => {
          // A colour counts as available if any size is; any other option is checked against the colour chosen.
          const test = index === this.colorIndex ? values.map(() => null) : values.slice();
          test[index] = input.value;
          const available = this.variants.some(
            (variant) => variant.available && test.every((value, i) => value === null || variant.values[i] === value)
          );
          // A sold-out size cannot be picked (unless it is already chosen, so the shopper sees why the
          // button says Sold out). A sold-out colour stays pickable, so its photo can still be seen.
          input.disabled = index !== this.colorIndex && !available && !input.checked;
          input.closest('label').toggleAttribute('data-unavailable', !available);
        });
      });
    }

    refreshPrice(values) {
      let shown = this.variant;
      let from = false;
      if (!shown) {
        // No size yet: the lowest price among the variants that fit what is chosen so far.
        const pool = this.variants.filter((variant) =>
          values.every((value, index) => value === null || variant.values[index] === value)
        );
        if (!pool.length) return;
        shown = pool.reduce((lowest, variant) => (variant.price < lowest.price ? variant : lowest), pool[0]);
        from = pool.some((variant) => variant.price !== shown.price);
      }
      const onSale = shown.compare > shown.price;
      setHidden(this.priceFrom, !from);
      setHidden(this.priceCompare, !onSale);
      setHidden(this.priceCompareLabel, !onSale);
      const price = formatMoney(shown.price, this.moneyFormat);
      setText(this.priceCurrent, price);
      if (onSale) setText(this.priceCompare, formatMoney(shown.compare, this.moneyFormat));
      this.priceText = (from ? 'From ' : '') + price;
      setText(this.barPrice, this.priceText);
    }

    refreshButtons() {
      const soldOut = this.complete && (!this.variant || !this.variant.available);
      const label = this.busy ? 'Adding\u2026' : soldOut ? 'Sold out' : 'Add to cart';
      [this.addButton, this.barButton].forEach((button) => {
        if (!button) return;
        button.disabled = soldOut;
        if (this.busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        setText(button.querySelector('[data-label]'), label);
      });
    }

    // Colour in the address bar so a link keeps the choice. With no size there is no variant, so no parameter.
    refreshAddress() {
      try {
        const url = new URL(window.location.href);
        if (this.variant) url.searchParams.set('variant', this.variant.id);
        else url.searchParams.delete('variant');
        if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
      } catch (error) {
        /* The address bar is a nicety; never let it break the page. */
      }
    }

    refreshBarSelection(values) {
      if (!this.barSelection) return;
      const order = this.optionNames.map((name, index) => index);
      if (this.colorIndex > 0) order.unshift(...order.splice(this.colorIndex, 1));
      const parts = order.map((index) => values[index] || `Choose ${this.optionNames[index].toLowerCase()}`);
      setText(this.barSelection, this.single ? '' : parts.join(' \u00b7 '));
    }

    /* ---------- Gallery ---------- */

    onOptionChange(input) {
      const fieldset = input.closest('fieldset');
      fieldset.removeAttribute('data-invalid');
      this.setError('');
      this.update();
      if (Number(fieldset.dataset.optionIndex) === this.colorIndex) {
        this.showMedia(this.mediaForColor(input.value) || this.thumbs[0]);
        input.closest('label').scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    mediaForColor(color) {
      const variant = this.variants.find((item) => item.mediaId && item.values[this.colorIndex] === color);
      return variant ? this.thumbs.find((thumb) => thumb.dataset.mediaId === String(variant.mediaId)) : null;
    }

    showMedia(thumb) {
      if (!thumb || !this.stageImage) return;
      const src = thumb.dataset.src;
      this.stageImage.src = src;
      this.stageImage.srcset = STAGE_WIDTHS.map((width) => `${withWidth(src, width)} ${width}w`).join(', ');
      this.stageImage.alt = thumb.dataset.alt;
      this.stage.dataset.zoomSrc = thumb.dataset.zoomSrc;
      this.thumbs.forEach((item) => item.setAttribute('aria-pressed', String(item === thumb)));
      const color = this.mediaColors[thumb.dataset.mediaId];
      setText(this.caption, color ? `Shown in ${color}` : 'Product photo');
    }

    /* ---------- Dialogs ---------- */

    openDialog(dialog) {
      if (!dialog || dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.body.classList.add('lpc-product-locked');
    }

    openZoom() {
      if (!this.zoomDialog || !this.stage.dataset.zoomSrc) return;
      this.zoomImage.src = this.stage.dataset.zoomSrc;
      this.zoomImage.alt = this.stageImage ? this.stageImage.alt : '';
      this.openDialog(this.zoomDialog);
    }

    openFinder() {
      if (!this.finder) return;
      const size = this.sizeIndex >= 0 ? this.values[this.sizeIndex] : null;
      this.finder.querySelectorAll('tbody tr').forEach((row) => {
        const current = size !== null && row.dataset.size.toLowerCase() === size.toLowerCase();
        row.toggleAttribute('data-current', current);
        if (current) row.setAttribute('aria-current', 'true');
        else row.removeAttribute('aria-current');
      });
      this.openDialog(this.finder);
    }

    /* Long titles on one line (PO): scale the font so the measured text fills the column, within the
       --lpc-title-min/max the CSS sets. A title too long even at the minimum wraps at --lpc-title-wrap
       instead of shrinking into unreadable sizes. */
    fitTitle() {
      const title = this.titleEl;
      if (!title) return;
      title.style.fontSize = '';
      title.dataset.fit = 'line'; // nowrap while measuring
      const style = getComputedStyle(title);
      const max = parseFloat(style.getPropertyValue('--lpc-title-max')) || 36;
      const min = parseFloat(style.getPropertyValue('--lpc-title-min')) || 14;
      const wrap = parseFloat(style.getPropertyValue('--lpc-title-wrap')) || min;
      const current = parseFloat(style.fontSize);
      const available = title.clientWidth;
      const range = document.createRange();
      range.selectNodeContents(title);
      const textWidth = range.getBoundingClientRect().width;
      if (!available || !textWidth || !current) {
        title.removeAttribute('data-fit');
        return;
      }
      // Start from the proportional estimate; glyph widths don't scale exactly linearly, so the
      // rendered width has the final say, in quarter-pixel steps down to the minimum.
      let size = Math.max(min, Math.min(max, Math.floor(((current * available) / textWidth) * 4) / 4));
      title.style.fontSize = size + 'px';
      while (title.scrollWidth > title.clientWidth && size > min) {
        size = Math.max(min, size - 0.25);
        title.style.fontSize = size + 'px';
      }
      if (title.scrollWidth <= title.clientWidth) return;
      // Too long for one line even at the minimum: wrap, at the largest size up to --lpc-title-wrap
      // that still keeps it to two lines.
      title.dataset.fit = 'wrap';
      size = Math.max(min, wrap);
      title.style.fontSize = size + 'px';
      while (title.clientHeight > parseFloat(style.lineHeight) * 2.5 && size > min) {
        size = Math.max(min, size - 0.5);
        title.style.fontSize = size + 'px';
      }
    }

    /* From the fit finder's "Choose size" button (assets/lpc-fit-finder.js). */
    selectSize(size) {
      if (!size || this.sizeIndex < 0) return;
      const inputs = [...this.querySelectorAll('[data-option-index="' + this.sizeIndex + '"] .lpc-product__input')];
      const input = inputs.find((item) => item.value.toLowerCase() === String(size).toLowerCase() && !item.disabled);
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.finder && this.finder.open) this.finder.close();
      const add = this.querySelector('[data-add]');
      if (add) add.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    setUnit(unit) {
      this.finder.dataset.unit = unit;
      this.finder.querySelectorAll('[data-unit-btn]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.unitBtn === unit));
      });
    }

    onClick(event) {
      const target = event.target.closest(
        '[data-zoom-open], [data-thumb], [data-zoom-close], [data-open-size-finder], [data-finder-close], [data-unit-btn], [data-bar-add]'
      );
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-zoom-open]')) this.openZoom();
      else if (target.matches('[data-thumb]')) this.showMedia(target);
      else if (target.matches('[data-zoom-close]')) this.zoomDialog.close();
      else if (target.matches('[data-open-size-finder]')) this.openFinder();
      else if (target.matches('[data-finder-close]')) this.finder.close();
      else if (target.matches('[data-unit-btn]')) this.setUnit(target.dataset.unitBtn);
      else if (target.matches('[data-bar-add]')) this.add(target);
    }

    /* ---------- Add to cart ---------- */

    setError(message) {
      setText(this.errorBox, message);
    }

    // No size (or other option) chosen yet: say so, and take the shopper to it.
    askForOption(fromBar) {
      const index = this.values.indexOf(null);
      const fieldset = this.fieldsets[index];
      if (!fieldset) return;
      this.setError(`Choose your ${this.optionNames[index].toLowerCase()} first.`);
      fieldset.setAttribute('data-invalid', '');
      const first = fieldset.querySelector('input:not(:disabled)');
      if (fromBar) fieldset.scrollIntoView({ block: 'center', behavior: calmMotion() ? 'auto' : 'smooth' });
      if (first) first.focus({ preventScroll: fromBar });
    }

    async add(trigger) {
      if (this.busy) return;
      if (!this.complete) {
        this.askForOption(trigger === this.barButton);
        return;
      }
      if (!this.variant || !this.variant.available) return;

      this.busy = true;
      this.setError('');
      this.refreshButtons();

      // True only when the app's own script is running; otherwise the paths below are Dawn's, unchanged.
      const supercart = !!(window.lpcCartBubble && window.lpcCartBubble.active());
      const drawer = supercart ? null : document.querySelector('cart-drawer');
      const body = { id: this.variant.id, quantity: 1 };
      if (supercart) {
        // SuperCart opens itself on the add; ask back only for the header cart count.
        body.sections = 'cart-icon-bubble';
        body.sections_url = window.location.pathname;
      }
      if (drawer) {
        body.sections = (typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble']
        ).join(',');
        body.sections_url = window.location.pathname;
      }

      let cart = null;
      let problem = ADD_ERROR;
      try {
        const root = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const response = await fetch(`${root}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (response.ok && !data.status) cart = data;
        else problem = data.description || data.message || ADD_ERROR;
      } catch (error) {
        /* Network trouble: keep the plain message. */
      }

      this.busy = false;
      this.refreshButtons();

      if (!cart) {
        this.setError(problem);
        return;
      }
      if (drawer && typeof drawer.renderContents === 'function') {
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);
        // renderContents clears "empty" on the inner panel only; the drawer itself must lose it too
        // (Dawn's product form does the same), or an empty-cart drawer opens showing the empty state.
        drawer.classList.remove('is-empty');
        // Tell the rest of the theme, as Dawn's own product form does.
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'lpc-product',
            productVariantId: this.variant.id,
            cartData: cart,
          });
        }
        drawer.renderContents(cart);
      } else if (supercart) {
        // No redirect and no click on the cart icon: SuperCart is already open.
        window.lpcCartBubble.paint(cart.sections && cart.sections['cart-icon-bubble']);
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'lpc-product',
            productVariantId: this.variant.id,
            cartData: cart,
          });
        }
        window.lpcCartBubble.expectOpen();
      } else {
        window.location = (window.routes && window.routes.cart_url) || '/cart';
      }
    }

    /* ---------- Phone purchase bar ---------- */

    watchBar() {
      if (!this.bar || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        // "Passed" = the button is off screen above us, not below.
        this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.syncBar();
      });
      this.observer.observe(this.addButton);
      phone.addEventListener('change', this.onPhoneChange);
    }

    syncBar() {
      if (!this.bar) return;
      const show = this.passed && phone.matches;
      this.bar.hidden = !show;
      document.body.classList.toggle('lpc-product-bar-on', show);
    }
  }

  customElements.define('lpc-product', LpcProduct);
})();
